import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@/components/base/Button";
import { buildDisbursementReport } from "@/services/financeService";
import { exportDisbursementPdf } from "@/utils/disbursementPdf";
import type { Charge, ChargeCategory, Cycle, Transaction } from "@/types";
import DisbursementSheet from "./DisbursementSheet";

interface Props {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
  charges: Charge[];
  chargeCategories: ChargeCategory[];
  cycles: Cycle[];
  schoolName: string;
  schoolYear: string;
}

export default function DisbursementPrintModal({
  open,
  onClose,
  transactions,
  charges,
  chargeCategories,
  cycles,
  schoolName,
  schoolYear,
}: Props) {
  const [scope, setScope] = useState("all");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // Un rôle limité à un cycle ne peut imprimer que ce cycle. "Vue globale" n'a
  // de sens que pour un rôle global disposant des deux cycles.
  useEffect(() => {
    if (!open) return;
    setScope(cycles.length > 1 ? "all" : cycles[0]?.id ?? "all");
  }, [open, cycles]);

  if (!open) return null;

  const report = buildDisbursementReport(transactions, charges, chargeCategories, scope);
  const scopeLabel =
    scope === "all" ? "Vue globale (Primaire + Secondaire)" : cycles.find((c) => c.id === scope)?.label ?? "";

  const handlePdf = () => exportDisbursementPdf(report, { schoolName, schoolYear, scopeLabel });

  return createPortal(
    <div className="print-portal fixed inset-0 z-[110] overflow-y-auto bg-foreground-950/45 backdrop-blur-[2px]">
      <div className="no-print sticky top-0 z-10 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center">
              <i className="ri-printer-line text-lg"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold font-heading text-foreground-950">
                Tableau prévisionnel des décaissements
              </h3>
              <p className="text-xs text-foreground-500">Aperçu prêt à imprimer — Session {schoolYear}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
            >
              {cycles.length > 1 && <option value="all">Vue globale</option>}
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <Button variant="outline" icon="ri-file-pdf-2-line" onClick={handlePdf}>
              Exporter en PDF
            </Button>
            <Button icon="ri-printer-line" onClick={() => window.print()}>
              Imprimer
            </Button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="print-pad px-4 py-6 md:px-8 md:py-10">
        <div className="mx-auto w-full max-w-[900px]">
          <DisbursementSheet
            report={report}
            schoolName={schoolName}
            schoolYear={schoolYear}
            scopeLabel={scopeLabel}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}